/**
 * Dual-thumb range slider for collection filters.
 * Handles two modes:
 *   1. Native price_range filters (Price, kW, etc. if S&D supports range)
 *   2. List-type numeric filters rendered as sliders (Dimensions)
 *      — selects all individual values within the chosen range
 *
 * Debounces URL navigation so the filter triggers after user stops dragging.
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 600;

  // There are TWO mobile filter drawers in this theme: the legacy one below
  // (openDrawer, which sets deferredMode) and `pf-drawer` in
  // sections/static-collection.liquid, which is the one actually in use. The legacy
  // drawer, its overlay/apply bar and its sort modal only bind when no
  // [data-pf-drawer] element is on the page (see pfDrawerActive in
  // initMobileFilterAndSort). Only the former ever set deferredMode, so on
  // mobile every slider committed the moment you released it — the drawer closed and
  // the page reloaded mid-selection instead of waiting for Apply. The pf-drawer marks
  // <body class="pf-drawer-open">, so honour that too.
  function isDeferred() {
    return deferredMode || document.body.classList.contains('pf-drawer-open');
  }

  // While deferred, record what this group WOULD have applied so the drawer's Apply
  // button can collect it. Stored per filter group as a ready-made query fragment,
  // which keeps the value-enumeration logic here rather than duplicating it in the
  // drawer. `names` are the params this group owns, so we never pick up other filters.
  function stashPendingRange(container, url, names) {
    var group = container.closest ? container.closest('[data-filter-group]') : null;
    if (!group) return;
    var sp;
    try { sp = new URL(url, window.location.origin).searchParams; } catch (e) { return; }
    var parts = [];
    for (var i = 0; i < names.length; i++) {
      if (!names[i]) continue;
      var vals = sp.getAll(names[i]);
      for (var j = 0; j < vals.length; j++) {
        parts.push(encodeURIComponent(names[i]) + '=' + encodeURIComponent(vals[j]));
      }
    }
    group.setAttribute('data-pending-range-params', parts.join('&'));
    document.dispatchEvent(new CustomEvent('tt:pending-range-change'));
  }

  // Taken from the currency span Liquid already renders next to the price inputs,
  // so the ticks match the store's currency instead of hardcoding a symbol.
  var CURRENCY_SYMBOL = (function () {
    var el = document.querySelector('.collection-filters__filter-range-currency');
    var s = el && el.textContent ? el.textContent.trim() : '';
    return s || '$';
  })();

  // ── List-view CSS fix: show current price, hide only compare-at ──
  (function () {
    var style = document.createElement('style');
    style.textContent =
      '.productgrid-listview .productitem--info .productitem__price { display: flex !important; order: 1; }' +
      '.productgrid-listview .productitem--info .productitem__price .price__compare-at.visible { display: none !important; }' +
      '.productgrid-listview .productitem--info .productitem--specs { order: 2; }';
    document.head.appendChild(style);
  })();

  // ════════════════════════════════════════════
  // Mobile deferred-apply mode
  // When true, filter clicks and slider changes
  // update the UI but DON'T navigate — the user
  // taps "Apply Filters" to commit all at once.
  // ════════════════════════════════════════════
  var deferredMode = false;

  // ════════════════════════════════════════════
  // Native price_range slider (Price, kW, etc.)
  // ════════════════════════════════════════════
  function initNativeRangeSlider(container) {
    // rangeGroup may be a parent OR a sibling of the slider container
    var rangeGroup = container.closest('[data-filter-group-range]');
    if (!rangeGroup) {
      // Price slider: [data-filter-group-range] is a sibling, find via shared parent
      var form = container.closest('form, [data-accordion-content], [data-filter-group]');
      if (form) rangeGroup = form.querySelector('[data-filter-group-range]');
    }
    if (!rangeGroup) return;

    var thumbMin = container.querySelector('[data-range-thumb="min"]');
    var thumbMax = container.querySelector('[data-range-thumb="max"]');
    var fill = container.querySelector('[data-range-fill]');
    var ticksEl = container.querySelector('[data-range-ticks]');

    var inputMin = rangeGroup.querySelector('[data-range-input="min"]');
    var inputMax = rangeGroup.querySelector('[data-range-input="max"]');

    if (!thumbMin || !thumbMax || !inputMin || !inputMax) return;

    var absMin = parseFloat(thumbMin.min) || 0;
    var absMax = parseFloat(thumbMin.max) || 100;

    var nameForUnits = inputMin.getAttribute('name') || inputMax.getAttribute('name') || '';
    var isPriceSlider = nameForUnits.indexOf('price') !== -1;
    generateTicks(ticksEl, absMin, absMax, isPriceSlider);

    function updateFill() {
      // Keep the spoken value in step with the painted one (WCAG 4.1.2).
      // updateFill is the single funnel every value change already passes
      // through, so this cannot drift out of sync with the fill bar.
      setThumbValueText(thumbMin, isPriceSlider);
      setThumbValueText(thumbMax, isPriceSlider);

      var minVal = parseFloat(thumbMin.value);
      var maxVal = parseFloat(thumbMax.value);
      var range = absMax - absMin;
      if (range <= 0 || !fill) return;
      fill.style.left = ((minVal - absMin) / range) * 100 + '%';
      fill.style.width = ((maxVal - minVal) / range) * 100 + '%';
    }

    var debounceTimer = null;
    function triggerFilter() {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        var url = new URL(window.location.href);
        var minName = inputMin.getAttribute('name');
        var maxName = inputMax.getAttribute('name');
        // Shopify's filter.v.price params are in WHOLE CURRENCY UNITS, not cents.
        // Measured: ?filter.v.price.lte=1500 returns 11 of 24 heaters ($801-$7,587),
        // while lte=150000 returns all 24. This used to multiply by 100, so dragging
        // the slider to $2,960 wrote lte=296000 ($296,000) and silently matched the
        // entire catalogue — the price slider never filtered anything.
        var multiplier = 1;
        if (minName) {
          if (parseFloat(inputMin.value) > absMin) url.searchParams.set(minName, Math.round(parseFloat(inputMin.value) * multiplier));
          else url.searchParams.delete(minName);
        }
        if (maxName) {
          if (parseFloat(inputMax.value) < absMax) url.searchParams.set(maxName, Math.round(parseFloat(inputMax.value) * multiplier));
          else url.searchParams.delete(maxName);
        }

        // Inside the mobile drawer nothing is committed until Apply is tapped.
        if (isDeferred()) { stashPendingRange(container, url.toString(), [minName, maxName]); return; }

        if (ajaxEnabled) { ajaxLoadCollection(url.toString()); return; }
        // Non-AJAX fallback: navigate directly. Don't rely on Empire's handler
        // (Shopify.queryParams may be undefined).
        saveScrollPosition();
        window.location.href = url.toString();
      }, DEBOUNCE_MS);
    }

    function onThumbInput(e) {
      var minVal = parseFloat(thumbMin.value);
      var maxVal = parseFloat(thumbMax.value);
      if (e.target === thumbMin && minVal > maxVal) { thumbMin.value = maxVal; minVal = maxVal; }
      if (e.target === thumbMax && maxVal < minVal) { thumbMax.value = minVal; maxVal = minVal; }
      inputMin.value = formatNum(minVal, absMax - absMin);
      inputMax.value = formatNum(maxVal, absMax - absMin);
      updateFill();
    }

    thumbMin.addEventListener('input', onThumbInput);
    thumbMax.addEventListener('input', onThumbInput);
    thumbMin.addEventListener('change', function () { triggerFilter(); });
    thumbMax.addEventListener('change', function () { triggerFilter(); });

    inputMin.addEventListener('input', function () { syncInputToThumb(inputMin, inputMax, thumbMin, thumbMax, absMin, absMax); updateFill(); });
    inputMax.addEventListener('input', function () { syncInputToThumb(inputMin, inputMax, thumbMin, thumbMax, absMin, absMax); updateFill(); });

    // Submit price filter on change (blur/Tab) or Enter
    inputMin.addEventListener('change', function () {
      syncInputToThumb(inputMin, inputMax, thumbMin, thumbMax, absMin, absMax);
      updateFill();
      triggerFilter();
    });
    inputMax.addEventListener('change', function () {
      syncInputToThumb(inputMin, inputMax, thumbMin, thumbMax, absMin, absMax);
      updateFill();
      triggerFilter();
    });
    inputMin.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); inputMin.blur(); }
    });
    inputMax.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); inputMax.blur(); }
    });

    // Initial state
    if (!inputMin.value) inputMin.value = formatNum(absMin, absMax - absMin);
    if (!inputMax.value) inputMax.value = formatNum(absMax, absMax - absMin);
    thumbMin.value = parseFloat(inputMin.value) || absMin;
    thumbMax.value = parseFloat(inputMax.value) || absMax;
    updateFill();
  }

  // ════════════════════════════════════════════
  // List-as-range slider (Dimensions, etc.)
  // Selects all individual filter values within
  // the chosen min–max range.
  // ════════════════════════════════════════════
  function initListRangeSlider(container) {
    var wrapper = container.closest('[data-list-range-slider]');
    if (!wrapper) return;

    var rangeGroup = wrapper.querySelector('[data-filter-group-range]');
    var thumbMin = container.querySelector('[data-range-thumb="min"]');
    var thumbMax = container.querySelector('[data-range-thumb="max"]');
    var fill = container.querySelector('[data-range-fill]');
    var ticksEl = container.querySelector('[data-range-ticks]');

    var inputMin = rangeGroup.querySelector('[data-list-range-input="min"]');
    var inputMax = rangeGroup.querySelector('[data-list-range-input="max"]');

    if (!thumbMin || !thumbMax || !inputMin || !inputMax) return;

    var absMin = parseFloat(wrapper.getAttribute('data-list-range-min')) || 0;
    var absMax = parseFloat(wrapper.getAttribute('data-list-range-max')) || 100;

    // Collect all value links — keep raw string for URL building
    var valueLinks = wrapper.querySelectorAll('[data-list-range-value]');
    var allValues = [];
    for (var i = 0; i < valueLinks.length; i++) {
      var rawVal = valueLinks[i].getAttribute('data-list-range-value');
      allValues.push({
        num: parseFloat(rawVal),
        raw: rawVal,
        el: valueLinks[i],
        active: valueLinks[i].hasAttribute('data-active'),
        name: valueLinks[i].getAttribute('data-name')
      });
    }
    allValues.sort(function (a, b) { return a.num - b.num; });

    // List-as-range sliders are dimensions and kW ratings — never prices.
    generateTicks(ticksEl, absMin, absMax, false);

    function updateFill() {
      // See the numeric slider above — aria-valuetext must track every move.
      setThumbValueText(thumbMin, false);
      setThumbValueText(thumbMax, false);

      var minVal = parseFloat(thumbMin.value);
      var maxVal = parseFloat(thumbMax.value);
      var range = absMax - absMin;
      if (range <= 0 || !fill) return;
      fill.style.left = ((minVal - absMin) / range) * 100 + '%';
      fill.style.width = ((maxVal - minVal) / range) * 100 + '%';
    }

    // Build URL that selects all values within [min, max]
    var debounceTimer = null;
    function triggerListFilter() {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        var minVal = parseFloat(inputMin.value) || absMin;
        var maxVal = parseFloat(inputMax.value) || absMax;

        // Start from current URL, strip existing filter params for this metafield
        var url = new URL(window.location.href);
        var paramName = allValues.length > 0 ? allValues[0].name : '';
        if (paramName) {
          url.searchParams.delete(paramName);
        }

        // Add params for all values within the range
        // Use raw string values (e.g. "7.0" not "7") so S&D recognizes them
        // (skip if range covers everything — no filter needed)
        var isFullRange = (minVal <= absMin && maxVal >= absMax);
        if (!isFullRange) {
          for (var i = 0; i < allValues.length; i++) {
            if (allValues[i].num >= minVal && allValues[i].num <= maxVal) {
              url.searchParams.append(paramName, allValues[i].raw);
            }
          }
        }

        // Inside the mobile drawer nothing is committed until Apply is tapped.
        if (isDeferred()) { stashPendingRange(container, url.toString(), [paramName]); return; }

        if (ajaxEnabled) { ajaxLoadCollection(url.toString()); return; }
        saveScrollPosition();
        window.location.href = url.toString();
      }, DEBOUNCE_MS);
    }

    function onThumbInput(e) {
      var minVal = parseFloat(thumbMin.value);
      var maxVal = parseFloat(thumbMax.value);
      if (e.target === thumbMin && minVal > maxVal) { thumbMin.value = maxVal; minVal = maxVal; }
      if (e.target === thumbMax && maxVal < minVal) { thumbMax.value = minVal; maxVal = minVal; }
      inputMin.value = formatNum(minVal, absMax - absMin);
      inputMax.value = formatNum(maxVal, absMax - absMin);
      updateFill();
    }

    thumbMin.addEventListener('input', onThumbInput);
    thumbMax.addEventListener('input', onThumbInput);
    thumbMin.addEventListener('change', function () { triggerListFilter(); });
    thumbMax.addEventListener('change', function () { triggerListFilter(); });

    inputMin.addEventListener('change', function () {
      syncInputToThumb(inputMin, inputMax, thumbMin, thumbMax, absMin, absMax);
      updateFill();
      triggerListFilter();
    });
    inputMax.addEventListener('change', function () {
      syncInputToThumb(inputMin, inputMax, thumbMin, thumbMax, absMin, absMax);
      updateFill();
      triggerListFilter();
    });

    // Initial state
    if (!inputMin.value) inputMin.value = formatNum(absMin, absMax - absMin);
    if (!inputMax.value) inputMax.value = formatNum(absMax, absMax - absMin);
    thumbMin.value = parseFloat(inputMin.value) || absMin;
    thumbMax.value = parseFloat(inputMax.value) || absMax;
    updateFill();
  }

  // ════════════════════════════════════════════
  // Shared helpers
  // ════════════════════════════════════════════

  function formatNum(val, range) {
    if (range > 10) return Math.round(val);
    return parseFloat(val.toFixed(1));
  }

  // Round tick values to a human 1 / 2 / 2.5 / 5 progression. Splitting the range
  // into equal quarters produced labels like "0 1897 3794 5690 7587", where four of
  // the five numbers are meaningless to a shopper. Ticks are positioned by their true
  // percentage (see .range-slider__tick in custom.css), so uneven spacing stays honest.
  function niceTickValues(absMin, absMax, target) {
    var range = absMax - absMin;
    if (!(range > 0)) return [absMin];
    var rough = range / target;
    var mag = Math.pow(10, Math.floor(Math.log(rough) / Math.LN10));
    var norm = rough / mag;
    var step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag;

    var out = [];
    for (var v = Math.ceil(absMin / step) * step; v <= absMax + step * 1e-6; v += step) {
      out.push(Math.round(v * 1000) / 1000);
    }
    // The endpoints are always labelled, so drop any generated tick that would crowd
    // them — otherwise a 11-31 range renders "30" and "31" on top of each other.
    while (out.length && absMax - out[out.length - 1] < step * 0.5) out.pop();
    while (out.length && out[0] - absMin < step * 0.5) out.shift();
    out.unshift(absMin);
    out.push(absMax);
    return out;
  }

  function formatTick(val, isPrice, range) {
    if (isPrice) {
      return CURRENCY_SYMBOL + Math.round(val).toLocaleString();
    }
    return (range > 10) ? String(Math.round(val)) : String(parseFloat(val.toFixed(1)));
  }

  /**
   * WCAG 4.1.2 — an <input type="range"> is announced by its raw `value`, so
   * screen readers read the price slider as "75028" and the height slider as
   * "100.8" with no unit. aria-valuetext replaces that with the human string
   * the sighted user sees on the ticks: "$75,028", "100.8 inches".
   *
   * Must be called on every value change, not just at init — aria-valuetext is
   * a static attribute and goes stale the moment the thumb moves.
   *
   * The unit comes out of the thumb's own aria-label ("Minimum Exterior Height
   * (Inches)") so a new dimension filter picks it up with no code change here.
   */
  function setThumbValueText(thumb, isPrice) {
    if (!thumb) return;
    var v = parseFloat(thumb.value);
    if (isNaN(v)) return;

    var text;
    if (isPrice) {
      text = CURRENCY_SYMBOL + Math.round(v).toLocaleString();
    } else {
      var label = thumb.getAttribute('aria-label') || '';
      var unitMatch = label.match(/\(([^)]+)\)\s*$/);
      var num = String(parseFloat(v.toFixed(1)));
      text = unitMatch ? num + ' ' + unitMatch[1].toLowerCase() : num;
    }
    thumb.setAttribute('aria-valuetext', text);
  }

  // Drop intermediate labels that would overlap once rendered. Measuring beats
  // guessing a spacing threshold: "$6,000" and "$7,587" collide in a 240px sidebar
  // but "60" and "94" do not, and the same range can be wider on another breakpoint.
  // The two endpoints are always kept — they define the scale.
  function pruneCollidingTicks(ticksEl) {
    var nodes = [].slice.call(ticksEl.children);
    if (nodes.length < 3) return;
    if (!ticksEl.getBoundingClientRect().width) return; // laid out later; see below
    var GAP = 6;
    var lastBox = nodes[nodes.length - 1].getBoundingClientRect();
    var keptRight = nodes[0].getBoundingClientRect().right;
    for (var i = 1; i < nodes.length - 1; i++) {
      var box = nodes[i].getBoundingClientRect();
      if (box.left < keptRight + GAP || box.right > lastBox.left - GAP) {
        nodes[i].remove();
      } else {
        keptRight = box.right;
      }
    }
  }

  function generateTicks(ticksEl, absMin, absMax, isPrice) {
    if (!ticksEl) return;
    var vals = niceTickValues(absMin, absMax, 4);
    var range = absMax - absMin;
    ticksEl.innerHTML = '';
    for (var i = 0; i < vals.length; i++) {
      var tick = document.createElement('span');
      tick.className = 'range-slider__tick';
      if (i === 0) tick.className += ' range-slider__tick--first';
      if (i === vals.length - 1) tick.className += ' range-slider__tick--last';
      tick.style.left = (range > 0 ? ((vals[i] - absMin) / range) * 100 : 0) + '%';
      tick.textContent = formatTick(vals[i], isPrice, range);
      ticksEl.appendChild(tick);
    }
    pruneCollidingTicks(ticksEl);

    // A group that is still collapsed (or in the closed mobile drawer) measures zero,
    // so nothing could be pruned above. Re-run once it has been opened and laid out.
    if (!ticksEl.getBoundingClientRect().width) {
      var group = ticksEl.closest('[data-filter-group]');
      if (group && !ticksEl.getAttribute('data-tick-prune-bound')) {
        ticksEl.setAttribute('data-tick-prune-bound', '1');
        group.addEventListener('click', function once() {
          setTimeout(function () { pruneCollidingTicks(ticksEl); }, 350);
          group.removeEventListener('click', once);
        });
      }
    }
  }

  function syncInputToThumb(inputMin, inputMax, thumbMin, thumbMax, absMin, absMax) {
    var minVal = parseFloat(inputMin.value) || absMin;
    var maxVal = parseFloat(inputMax.value) || absMax;
    minVal = Math.max(absMin, Math.min(minVal, absMax));
    maxVal = Math.max(absMin, Math.min(maxVal, absMax));
    if (minVal > maxVal) minVal = maxVal;
    thumbMin.value = minVal;
    thumbMax.value = maxVal;
  }

  // ════════════════════════════════════════════
  // Init
  // ════════════════════════════════════════════
  function initAll() {
    var sliders = document.querySelectorAll('[data-range-slider]');
    for (var i = 0; i < sliders.length; i++) {
      var slider = sliders[i];
      if (slider.getAttribute('data-is-list-range') === 'true') {
        initListRangeSlider(slider);
      } else {
        initNativeRangeSlider(slider);
      }
    }
  }

  // ════════════════════════════════════════════
  // Filter group accordion (collapse / expand)
  // ════════════════════════════════════════════
  function initAccordions() {
    var triggers = document.querySelectorAll('[data-filter-group-trigger]');
    for (var i = 0; i < triggers.length; i++) {
      var trigger = triggers[i];
      // Skip if already bound
      if (trigger.hasAttribute('data-accordion-bound')) continue;
      trigger.setAttribute('data-accordion-bound', '');

      // Set initial state: open (content visible)
      trigger.setAttribute('aria-expanded', 'true');

      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        var btn = this;
        var group = btn.closest('[data-filter-group]');
        if (!group) return;
        var content = group.querySelector('[data-accordion-content]');
        if (!content) return;

        var isOpen = btn.getAttribute('aria-expanded') === 'true';

        if (isOpen) {
          // Collapse: animate height to 0
          content.style.height = content.scrollHeight + 'px';
          content.offsetHeight; // force reflow
          content.style.overflow = 'hidden';
          content.style.transition = 'height 0.25s ease';
          content.style.height = '0';
          btn.setAttribute('aria-expanded', 'false');
        } else {
          // Expand: animate height from 0 to auto
          content.style.height = '0';
          content.style.overflow = 'hidden';
          content.style.transition = 'height 0.25s ease';
          content.style.height = content.scrollHeight + 'px';
          btn.setAttribute('aria-expanded', 'true');
          // After animation, remove inline styles so content flows naturally
          setTimeout(function () {
            content.style.height = '';
            content.style.overflow = '';
            content.style.transition = '';
          }, 260);
        }
      });
    }
  }

  // ════════════════════════════════════════════
  // Accordion state save / restore across AJAX
  // ════════════════════════════════════════════
  function saveAccordionStates() {
    var states = {};
    var triggers = document.querySelectorAll('[data-filter-group-trigger]');
    for (var i = 0; i < triggers.length; i++) {
      var group = triggers[i].closest('[data-filter-group]');
      if (!group) continue;
      var label = group.getAttribute('data-filter-group') || i.toString();
      states[label] = triggers[i].getAttribute('aria-expanded') === 'true';
    }
    return states;
  }

  function restoreAccordionStates(states) {
    if (!states) return;
    var triggers = document.querySelectorAll('[data-filter-group-trigger]');
    for (var i = 0; i < triggers.length; i++) {
      var group = triggers[i].closest('[data-filter-group]');
      if (!group) continue;
      var label = group.getAttribute('data-filter-group') || i.toString();
      if (label in states) {
        var shouldBeOpen = states[label];
        var content = group.querySelector('[data-accordion-content]');
        if (!content) continue;
        if (shouldBeOpen) {
          triggers[i].setAttribute('aria-expanded', 'true');
          content.style.height = '';
          content.style.overflow = '';
        } else {
          triggers[i].setAttribute('aria-expanded', 'false');
          content.style.height = '0';
          content.style.overflow = 'hidden';
        }
      }
    }
  }

  // ════════════════════════════════════════════
  // Mobile filter + sort fix
  // Empire's parent class (Oi) skips binding mobile
  // button handlers because it checks for
  // [data-productgrid-filters-content] which the
  // faceted filter template doesn't use. This
  // re-implements the filter drawer and sort modal.
  //
  // DEFERRED APPLY: on mobile, filter clicks don't
  // navigate immediately. Instead the user picks
  // multiple filters, then taps "Apply Filters".
  // ════════════════════════════════════════════
  function initMobileFilterAndSort() {
    // ────────────────────────────────────────────────
    // Empire's Oi class doesn't bind the mobile filter
    // or sort buttons for faceted filters (it checks for
    // [data-productgrid-filters-content] which doesn't
    // exist). This theme also has NO .modal--productgrid-sidebar
    // element — filter content lives entirely in
    // [data-productgrid-sidebar].
    //
    // On mobile, we show that sidebar as a fullscreen
    // drawer with deferred-apply (pick filters → tap Apply).
    // ────────────────────────────────────────────────

    // Only on mobile — desktop uses initDesktopAjax for AJAX filtering
    // and the sidebar is already visible as a column.
    if (window.innerWidth > 859) return;

    var filterBtn = document.querySelector('[data-productgrid-trigger-filters]');
    var sidebar = document.querySelector('[data-productgrid-sidebar]');
    if (!filterBtn || !sidebar) return;

    // Skip if already initialized
    if (filterBtn.hasAttribute('data-mobile-bound')) return;
    filterBtn.setAttribute('data-mobile-bound', '');

    var pfDrawerActive = !!document.querySelector('[data-pf-drawer]');

    if (!pfDrawerActive) {
      // ── State snapshot (for cancel / close without apply) ──
      var snapshot = null;

      function takeSnapshot() {
        snapshot = { activeLinks: [], rangeInputs: {}, listRangeThumbs: [] };

        var links = sidebar.querySelectorAll('[data-filter-input]');
        for (var i = 0; i < links.length; i++) {
          if (links[i].hasAttribute('data-filter-active')) {
            snapshot.activeLinks.push(links[i]);
          }
        }

        var rangeInputs = sidebar.querySelectorAll('input[data-filter-range]');
        for (var i = 0; i < rangeInputs.length; i++) {
          var name = rangeInputs[i].getAttribute('name');
          if (name) snapshot.rangeInputs[name] = rangeInputs[i].value;
        }

        var wrappers = sidebar.querySelectorAll('[data-list-range-slider]');
        for (var i = 0; i < wrappers.length; i++) {
          var tMin = wrappers[i].querySelector('[data-range-thumb="min"]');
          var tMax = wrappers[i].querySelector('[data-range-thumb="max"]');
          if (tMin && tMax) {
            snapshot.listRangeThumbs.push({ wrapper: wrappers[i], min: tMin.value, max: tMax.value });
          }
        }
      }

      function restoreSnapshot() {
        if (!snapshot) return;

        var links = sidebar.querySelectorAll('[data-filter-input]');
        for (var i = 0; i < links.length; i++) {
          var link = links[i];
          var li = link.closest('.collection-filters__filter-list-item');
          var wasActive = snapshot.activeLinks.indexOf(link) !== -1;
          if (wasActive) {
            link.setAttribute('data-filter-active', '');
            if (li) li.classList.add('collection-filters__filter-list-item--active');
          } else {
            link.removeAttribute('data-filter-active');
            if (li) li.classList.remove('collection-filters__filter-list-item--active');
          }
        }

        for (var name in snapshot.rangeInputs) {
          var input = sidebar.querySelector('input[name="' + name + '"]');
          if (input) input.value = snapshot.rangeInputs[name];
        }

        for (var i = 0; i < snapshot.listRangeThumbs.length; i++) {
          var s = snapshot.listRangeThumbs[i];
          var tMin = s.wrapper.querySelector('[data-range-thumb="min"]');
          var tMax = s.wrapper.querySelector('[data-range-thumb="max"]');
          var iMin = s.wrapper.querySelector('[data-list-range-input="min"]');
          var iMax = s.wrapper.querySelector('[data-list-range-input="max"]');
          var fill = s.wrapper.querySelector('[data-range-fill]');
          if (tMin) tMin.value = s.min;
          if (tMax) tMax.value = s.max;
          if (iMin) iMin.value = formatNum(parseFloat(s.min), 100);
          if (iMax) iMax.value = formatNum(parseFloat(s.max), 100);
          if (fill) {
            var absMin = parseFloat(s.wrapper.getAttribute('data-list-range-min'));
            var absMax = parseFloat(s.wrapper.getAttribute('data-list-range-max'));
            var range = absMax - absMin;
            if (range > 0) {
              fill.style.left  = ((parseFloat(s.min) - absMin) / range) * 100 + '%';
              fill.style.width = ((parseFloat(s.max) - parseFloat(s.min)) / range) * 100 + '%';
            }
          }
        }

        var nativeSliders = sidebar.querySelectorAll('[data-range-slider]:not([data-is-list-range])');
        for (var i = 0; i < nativeSliders.length; i++) {
          var sl = nativeSliders[i];
          var form = sl.closest('form');
          if (!form) continue;
          var rg = form.querySelector('[data-filter-group-range]');
          if (!rg) continue;
          var inMin = rg.querySelector('[data-range-input="min"]');
          var inMax = rg.querySelector('[data-range-input="max"]');
          var thMin = sl.querySelector('[data-range-thumb="min"]');
          var thMax = sl.querySelector('[data-range-thumb="max"]');
          var fill  = sl.querySelector('[data-range-fill]');
          if (inMin && thMin) thMin.value = inMin.value;
          if (inMax && thMax) thMax.value = inMax.value;
          if (fill && thMin && thMax) {
            var aMin = parseFloat(thMin.min) || 0;
            var aMax = parseFloat(thMin.max) || 100;
            var r = aMax - aMin;
            if (r > 0) {
              fill.style.left  = ((parseFloat(thMin.value) - aMin) / r) * 100 + '%';
              fill.style.width = ((parseFloat(thMax.value) - parseFloat(thMin.value)) / r) * 100 + '%';
            }
          }
        }
      }

      // ── Build the combined filter URL from current visual state ──
      function buildFilterUrl() {
        var base = window.location.pathname;
        var params = [];

        // 1) List filters — every link with data-filter-active
        var active = sidebar.querySelectorAll('[data-filter-input][data-filter-active]');
        for (var i = 0; i < active.length; i++) {
          var n = active[i].getAttribute('data-name');
          var v = active[i].getAttribute('data-value');
          if (n && v) params.push(encodeURIComponent(n) + '=' + encodeURIComponent(v));
        }

        // 2) List-as-range sliders — include all hidden values within range
        var listRanges = sidebar.querySelectorAll('[data-list-range-slider]');
        for (var i = 0; i < listRanges.length; i++) {
          var w = listRanges[i];
          var tMin = w.querySelector('[data-range-thumb="min"]');
          var tMax = w.querySelector('[data-range-thumb="max"]');
          if (!tMin || !tMax) continue;
          var minV = parseFloat(tMin.value);
          var maxV = parseFloat(tMax.value);
          var aMin = parseFloat(w.getAttribute('data-list-range-min'));
          var aMax = parseFloat(w.getAttribute('data-list-range-max'));
          if (minV <= aMin && maxV >= aMax) continue;
          var vals = w.querySelectorAll('[data-list-range-value]');
          for (var j = 0; j < vals.length; j++) {
            var raw = vals[j].getAttribute('data-list-range-value');
            var num = parseFloat(raw);
            if (num >= minV && num <= maxV) {
              params.push(encodeURIComponent(vals[j].getAttribute('data-name')) + '=' + encodeURIComponent(raw));
            }
          }
        }

        // 3) Native price / range — read inputs
        var priceForms = sidebar.querySelectorAll('.collection-filters__price-range-form');
        for (var i = 0; i < priceForms.length; i++) {
          var rg = priceForms[i].querySelector('[data-filter-group-range]');
          if (!rg) continue;
          var aMin = parseFloat(rg.getAttribute('data-range-min'));
          var aMax = parseFloat(rg.getAttribute('data-range-max'));
          var inputs = priceForms[i].querySelectorAll('input[data-filter-range]');
          for (var j = 0; j < inputs.length; j++) {
            var nm  = inputs[j].getAttribute('name');
            var val = parseFloat(inputs[j].value);
            var typ = inputs[j].getAttribute('data-range-input');
            if (!nm) continue;
            // Price params are whole currency units, not cents — see initNativeRangeSlider.
            var mult = 1;
            if (typ === 'min' && val > aMin)  params.push(encodeURIComponent(nm) + '=' + Math.round(val * mult));
            if (typ === 'max' && val < aMax)  params.push(encodeURIComponent(nm) + '=' + Math.round(val * mult));
          }
        }

        // 4) Preserve sort_by
        var cur = new URLSearchParams(window.location.search);
        var sort = cur.get('sort_by');
        if (sort) params.push('sort_by=' + encodeURIComponent(sort));

        return base + (params.length ? '?' + params.join('&') : '');
      }

      // ── Count how many filters are currently active ──
      function countActive() {
        var n = 0;
        n += sidebar.querySelectorAll('[data-filter-input][data-filter-active]').length;
        var pf = sidebar.querySelectorAll('.collection-filters__price-range-form');
        for (var i = 0; i < pf.length; i++) {
          var rg = pf[i].querySelector('[data-filter-group-range]');
          if (!rg) continue;
          var aMin = parseFloat(rg.getAttribute('data-range-min'));
          var aMax = parseFloat(rg.getAttribute('data-range-max'));
          var iMin = pf[i].querySelector('[data-range-input="min"]');
          var iMax = pf[i].querySelector('[data-range-input="max"]');
          if (iMin && parseFloat(iMin.value) > aMin) n++;
          if (iMax && parseFloat(iMax.value) < aMax) n++;
        }
        var lr = sidebar.querySelectorAll('[data-list-range-slider]');
        for (var i = 0; i < lr.length; i++) {
          var aMin = parseFloat(lr[i].getAttribute('data-list-range-min'));
          var aMax = parseFloat(lr[i].getAttribute('data-list-range-max'));
          var tMin = lr[i].querySelector('[data-range-thumb="min"]');
          var tMax = lr[i].querySelector('[data-range-thumb="max"]');
          if (tMin && tMax && (parseFloat(tMin.value) > aMin || parseFloat(tMax.value) < aMax)) n++;
        }
        return n;
      }

      // ── Clear All — reset every filter to off ──
      function clearAllFilters() {
        var links = sidebar.querySelectorAll('[data-filter-input]');
        for (var i = 0; i < links.length; i++) {
          links[i].removeAttribute('data-filter-active');
          var li = links[i].closest('.collection-filters__filter-list-item');
          if (li) li.classList.remove('collection-filters__filter-list-item--active');
        }
        var pf = sidebar.querySelectorAll('.collection-filters__price-range-form');
        for (var i = 0; i < pf.length; i++) {
          var rg = pf[i].querySelector('[data-filter-group-range]');
          if (!rg) continue;
          var aMin = rg.getAttribute('data-range-min');
          var aMax = rg.getAttribute('data-range-max');
          var iMin = pf[i].querySelector('[data-range-input="min"]');
          var iMax = pf[i].querySelector('[data-range-input="max"]');
          var tMin = pf[i].querySelector('[data-range-thumb="min"]');
          var tMax = pf[i].querySelector('[data-range-thumb="max"]');
          var fill = pf[i].querySelector('[data-range-fill]');
          if (iMin) iMin.value = aMin;
          if (iMax) iMax.value = aMax;
          if (tMin) tMin.value = aMin;
          if (tMax) tMax.value = aMax;
          if (fill) { fill.style.left = '0%'; fill.style.width = '100%'; }
        }
        var lr = sidebar.querySelectorAll('[data-list-range-slider]');
        for (var i = 0; i < lr.length; i++) {
          var aMin = lr[i].getAttribute('data-list-range-min');
          var aMax = lr[i].getAttribute('data-list-range-max');
          var tMin = lr[i].querySelector('[data-range-thumb="min"]');
          var tMax = lr[i].querySelector('[data-range-thumb="max"]');
          var iMin = lr[i].querySelector('[data-list-range-input="min"]');
          var iMax = lr[i].querySelector('[data-list-range-input="max"]');
          var fill = lr[i].querySelector('[data-range-fill]');
          if (tMin) tMin.value = aMin;
          if (tMax) tMax.value = aMax;
          if (iMin) iMin.value = aMin;
          if (iMax) iMax.value = aMax;
          if (fill) { fill.style.left = '0%'; fill.style.width = '100%'; }
        }
        updateApplyBtn();
      }

      // ── Apply bar (sticky bottom of drawer) ──
      var applyBar = null;

      function createApplyBar() {
        var bar = document.createElement('div');
        bar.className = 'mobile-filter-apply-bar';
        bar.style.cssText =
          'position:fixed;bottom:0;left:0;width:100%;z-index:10000;' +
          'background:#fff;display:flex;align-items:center;justify-content:space-between;' +
          'padding:12px 20px;box-shadow:0 -2px 8px rgba(0,0,0,0.1);gap:12px;';

        var clearBtn = document.createElement('button');
        clearBtn.className = 'mobile-filter-clear-btn';
        clearBtn.type = 'button';
        clearBtn.textContent = 'Clear All';
        clearBtn.style.cssText =
          'background:none;border:1px solid #ccc;padding:10px 16px;' +
          'font-size:14px;cursor:pointer;border-radius:4px;white-space:nowrap;';
        clearBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopImmediatePropagation();
          window.location.href = window.location.pathname;
        });

        var applyBtn = document.createElement('button');
        applyBtn.className = 'mobile-filter-apply-btn';
        applyBtn.type = 'button';
        applyBtn.textContent = 'Apply Filters';
        applyBtn.style.cssText =
          'background:#19733e;color:#fff;border:none;padding:10px 20px;' +
          'font-size:14px;cursor:pointer;border-radius:4px;flex:1;white-space:nowrap;';
        applyBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopImmediatePropagation();
          var url = buildFilterUrl();
          if (url === window.location.pathname + window.location.search || url === window.location.href) {
            closeDrawer();
          } else {
            window.location.href = url;
          }
        });

        bar.appendChild(clearBtn);
        bar.appendChild(applyBtn);
        return bar;
      }

      function updateApplyBtn() {
        if (!applyBar) return;
        var btn = applyBar.querySelector('.mobile-filter-apply-btn');
        if (!btn) return;
        var c = countActive();
        btn.textContent = c > 0 ? 'Apply Filters (' + c + ')' : 'Apply Filters';
      }

      // ── Hide / show floating widgets (Crisp chat, Google store badge) ──
      // These have extreme z-index values and overlay the drawer/sort modal.
      function hideFloatingWidgets() {
        var crisp = document.querySelector('#crisp-chatbox');
        if (crisp) crisp.style.setProperty('display', 'none', 'important');
        var gw = document.querySelector('#google-merchantwidget-iframe-wrapper');
        if (gw) gw.style.setProperty('display', 'none', 'important');
      }

      function showFloatingWidgets() {
        var crisp = document.querySelector('#crisp-chatbox');
        if (crisp) crisp.style.removeProperty('display');
        var gw = document.querySelector('#google-merchantwidget-iframe-wrapper');
        if (gw) gw.style.removeProperty('display');
      }

      // ── Overlay ──
      var overlay = document.createElement('div');
      overlay.className = 'mobile-filter-overlay';
      overlay.style.cssText =
        'position:fixed;top:0;left:0;width:100%;height:100%;' +
        'background:rgba(0,0,0,0.4);z-index:9998;display:none;';
      document.body.appendChild(overlay);
      overlay.addEventListener('click', function () { closeDrawer(); });

      // ── Open / close the sidebar as a fullscreen drawer ──
      function openDrawer() {
        if (deferredMode) return; // already open

        deferredMode = true;
        takeSnapshot();

        sidebar.style.cssText =
          'display:block !important;position:fixed;top:0;left:0;' +
          'width:100%;height:100%;z-index:9999;' +
          'background:#fff;overflow-y:auto;padding:20px 20px 80px;';
        sidebar.classList.add('filter-drawer--open');

        overlay.style.display = 'block';
        document.body.style.overflow = 'hidden';
        hideFloatingWidgets();

        // Inject close button once
        if (!sidebar.querySelector('.mobile-filter-close')) {
          var closeBtn = document.createElement('button');
          closeBtn.className = 'mobile-filter-close';
          closeBtn.setAttribute('aria-label', 'Close filters');
          closeBtn.innerHTML = '<svg width="20" height="20" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17 1L1 17" stroke="currentColor" stroke-width="1.75" stroke-linejoin="round"/><path d="M1 1L17 17" stroke="currentColor" stroke-width="1.75" stroke-linejoin="round"/></svg>';
          closeBtn.style.cssText =
            'position:sticky;top:0;float:right;background:none;' +
            'border:none;font-size:24px;cursor:pointer;padding:8px;z-index:1;';
          closeBtn.addEventListener('click', function () { closeDrawer(); });
          sidebar.insertBefore(closeBtn, sidebar.firstChild);
        }

        // Inject apply bar into body (not sidebar) so position:fixed works reliably
        if (!document.querySelector('.mobile-filter-apply-bar')) {
          applyBar = createApplyBar();
          document.body.appendChild(applyBar);
        }
        updateApplyBtn();
      }

      function closeDrawer() {
        if (deferredMode) {
          restoreSnapshot();
          deferredMode = false;
        }

        sidebar.style.cssText = '';
        sidebar.classList.remove('filter-drawer--open');

        // Remove apply bar from body
        var bar = document.querySelector('.mobile-filter-apply-bar');
        if (bar) bar.remove();
        applyBar = null;

        overlay.style.display = 'none';
        document.body.style.overflow = '';
        showFloatingWidgets();
      }

      // ── Filter button opens the drawer ──
      filterBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        if (sidebar.classList.contains('filter-drawer--open')) {
          closeDrawer();
        } else {
          openDrawer();
        }
      });

      // ── Intercept filter-link clicks in deferred mode ──
      // Uses capture phase + stopImmediatePropagation to block
      // Empire's Kn handler from navigating.
      sidebar.addEventListener('click', function (e) {
        if (!deferredMode) return;

        // ── Handle "Clear all" pill or link ──
        var clearAll = e.target.closest('[data-clear-all-pill], .filter-clear-all-link');
        if (clearAll) {
          e.preventDefault();
          e.stopImmediatePropagation();
          clearAllFilters();
          // Also remove all pill elements
          var pills = sidebar.querySelectorAll('.active-filter-group__item-wrapper');
          for (var p = 0; p < pills.length; p++) pills[p].remove();
          var clearLink = sidebar.querySelector('.filter-clear-all-link');
          if (clearLink) clearLink.style.display = 'none';
          updateApplyBtn();
          return;
        }

        var link = e.target.closest('[data-filter-input]');
        if (!link) return;

        // Skip hardcoded disabled placeholders (href="#")
        if (link.hasAttribute('data-disabled') && link.getAttribute('href') === '#') return;

        e.preventDefault();
        e.stopImmediatePropagation();

        // ── Active filter pill clicked ──
        if (link.hasAttribute('data-active-pill')) {
          var pillType = link.getAttribute('data-active-pill');
          var pillName = link.getAttribute('data-name');
          var pillValue = link.getAttribute('data-value');

          if (pillType === 'list' && pillName && pillValue) {
            var match = sidebar.querySelector(
              '[data-filter-input][data-name="' + pillName + '"][data-value="' + pillValue + '"]:not([data-active-pill])'
            );
            if (match) {
              match.removeAttribute('data-filter-active');
              var mLi = match.closest('.collection-filters__filter-list-item');
              if (mLi) mLi.classList.remove('collection-filters__filter-list-item--active');
            }
          } else if (pillType === 'price-range') {
            var pf = sidebar.querySelectorAll('.collection-filters__price-range-form');
            for (var i = 0; i < pf.length; i++) {
              var rg = pf[i].querySelector('[data-filter-group-range]');
              if (!rg) continue;
              var aMin = rg.getAttribute('data-range-min');
              var aMax = rg.getAttribute('data-range-max');
              var iMin = pf[i].querySelector('[data-range-input="min"]');
              var iMax = pf[i].querySelector('[data-range-input="max"]');
              var tMin = pf[i].querySelector('[data-range-thumb="min"]');
              var tMax = pf[i].querySelector('[data-range-thumb="max"]');
              var fill = pf[i].querySelector('[data-range-fill]');
              if (iMin) iMin.value = aMin;
              if (iMax) iMax.value = aMax;
              if (tMin) tMin.value = aMin;
              if (tMax) tMax.value = aMax;
              if (fill) { fill.style.left = '0%'; fill.style.width = '100%'; }
            }
          } else if (pillType === 'list-range' && pillName) {
            var lr = sidebar.querySelectorAll('[data-list-range-slider]');
            for (var i = 0; i < lr.length; i++) {
              var firstVal = lr[i].querySelector('[data-list-range-value]');
              if (!firstVal || firstVal.getAttribute('data-name') !== pillName) continue;
              var aMin = lr[i].getAttribute('data-list-range-min');
              var aMax = lr[i].getAttribute('data-list-range-max');
              var tMin = lr[i].querySelector('[data-range-thumb="min"]');
              var tMax = lr[i].querySelector('[data-range-thumb="max"]');
              var iMin = lr[i].querySelector('[data-list-range-input="min"]');
              var iMax = lr[i].querySelector('[data-list-range-input="max"]');
              var fill = lr[i].querySelector('[data-range-fill]');
              if (tMin) tMin.value = aMin;
              if (tMax) tMax.value = aMax;
              if (iMin) iMin.value = aMin;
              if (iMax) iMax.value = aMax;
              if (fill) { fill.style.left = '0%'; fill.style.width = '100%'; }
            }
          }

          var pillLi = link.closest('.active-filter-group__item-wrapper');
          if (pillLi) pillLi.remove();

          var remainingPills = sidebar.querySelectorAll('.active-filter-group__item-wrapper');
          if (remainingPills.length === 0) {
            var clearLink2 = sidebar.querySelector('.filter-clear-all-link');
            if (clearLink2) clearLink2.style.display = 'none';
          }

          updateApplyBtn();
          return;
        }

        // ── Standard filter checkbox toggle ──
        var li = link.closest('.collection-filters__filter-list-item');
        if (link.hasAttribute('data-filter-active')) {
          link.removeAttribute('data-filter-active');
          if (li) li.classList.remove('collection-filters__filter-list-item--active');
          removePillFor(link.getAttribute('data-name'), link.getAttribute('data-value'));
        } else {
          link.setAttribute('data-filter-active', '');
          if (li) li.classList.add('collection-filters__filter-list-item--active');
        }

        updateApplyBtn();
      }, true);

      // ── Remove an active pill by matching data-name + data-value ──
      function removePillFor(name, value) {
        if (!name || !value) return;
        var pill = sidebar.querySelector(
          '[data-active-pill="list"][data-name="' + name + '"][data-value="' + value + '"]'
        );
        if (pill) {
          var li = pill.closest('.active-filter-group__item-wrapper');
          if (li) li.remove();
        }
        var remaining = sidebar.querySelectorAll('.active-filter-group__item-wrapper');
        if (remaining.length === 0) {
          var clearLink = sidebar.querySelector('.filter-clear-all-link');
          if (clearLink) clearLink.style.display = 'none';
        }
      }

      // ── Prevent price-range form submission in deferred mode ──
      var priceForms = sidebar.querySelectorAll('.collection-filters__price-range-form');
      for (var fi = 0; fi < priceForms.length; fi++) {
        priceForms[fi].addEventListener('submit', function (e) {
          if (deferredMode) e.preventDefault();
        });
      }

      // ── Sort button (Empire doesn't bind it for faceted filters) ──
      var sortBtn = document.querySelector('[data-productgrid-modal-sort]');
      var sortModal = document.querySelector('.utils-sortby--modal');

      if (sortBtn && sortModal) {
        sortBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();

          var isOpen = sortModal.style.display === 'block';
          if (isOpen) {
            sortModal.style.cssText = '';
            sortModal.style.display = '';
            sortModal.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';
            showFloatingWidgets();
            return;
          }

          sortModal.style.cssText =
            'display:block;position:fixed;bottom:0;left:0;' +
            'width:100%;z-index:9999;background:#fff;' +
            'padding:20px;box-shadow:0 -4px 12px rgba(0,0,0,0.15);' +
            'border-radius:12px 12px 0 0;max-height:60vh;overflow-y:auto;';
          sortModal.setAttribute('aria-hidden', 'false');
          document.body.style.overflow = 'hidden';
          hideFloatingWidgets();
        });

        // Close modal after sort option selected
        var sortOptions = sortModal.querySelectorAll('button');
        for (var si = 0; si < sortOptions.length; si++) {
          sortOptions[si].addEventListener('click', function () {
            setTimeout(function () {
              sortModal.style.cssText = '';
              sortModal.style.display = '';
              sortModal.setAttribute('aria-hidden', 'true');
              document.body.style.overflow = '';
              showFloatingWidgets();
            }, 150);
          });
        }
      }
    }

    // ── Sort select fallback (non-modal, only when AJAX is NOT active) ──
    // When initDesktopAjax runs it replaces this with an AJAX-powered handler.
    // This fallback only fires on mobile / non-AJAX paths.
    var sortSelect = document.querySelector('[data-productgrid-trigger-sort]');
    if (sortSelect && !sortSelect.hasAttribute('data-sort-bound')) {
      if (window.jQuery) { jQuery(sortSelect).off('change.collection'); }
      sortSelect.setAttribute('data-sort-bound', 'fallback');
      sortSelect.addEventListener('change', function () {
        saveScrollPosition();
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', this.value);
        window.location.href = url.toString();
      });
    }
  }

  // ════════════════════════════════════════════
  // Scroll-position save (fallback for mobile
  // and any non-AJAX navigations)
  // ════════════════════════════════════════════
  function saveScrollPosition() {
    try { sessionStorage.setItem('filterScrollY', String(window.scrollY)); } catch (e) {}
  }

  // ════════════════════════════════════════════
  // Shopify.queryParams safety net
  // Empire's Kn class calls Object.entries(Shopify.queryParams)
  // which throws if undefined. Ensure it always exists.
  // ════════════════════════════════════════════
  (function ensureQueryParams() {
    if (typeof Shopify === 'undefined') window.Shopify = {};
    if (typeof Shopify.queryParams === 'undefined') {
      Shopify.queryParams = {};
      var allowed = ['view', 'sort_by', 'grid_list'];
      var search = location.search.substr(1).split('&');
      if (location.search.length) {
        search.forEach(function (s) {
          var parts = s.split('=');
          if (parts.length > 1 && allowed.indexOf(parts[0]) !== -1) {
            Shopify.queryParams[decodeURIComponent(parts[0])] = decodeURIComponent(parts[1]);
          }
        });
      }
    }
  })();

  // ════════════════════════════════════════════
  // Desktop AJAX filtering
  // Uses Shopify's ?sections= API to fetch only
  // the collection section HTML. Swaps sidebar,
  // grid, utils, pagination, and masthead
  // individually for a seamless experience.
  // ════════════════════════════════════════════
  var ajaxEnabled = false;

  function getSectionId() {
    var sectionEl = document.querySelector('[data-section-type="static-collection-faceted-filters"]');
    if (!sectionEl) return null;
    var shopifySection = sectionEl.closest('.shopify-section');
    if (!shopifySection || !shopifySection.id) return null;
    return shopifySection.id.replace('shopify-section-', '');
  }

  function ajaxLoadCollection(url, isPopstate) {
    // Clean malformed grid_list param (Empire compounds it: grid-view=grid-view=...)
    try {
      var cleanUrl = new URL(url, location.origin);
      var gl = cleanUrl.searchParams.get('grid_list');
      if (gl && gl.indexOf('=') !== -1) {
        cleanUrl.searchParams.set('grid_list', gl.split('=')[0]);
      }
      // Filter-pill, clear-all and filter-link hrefs are rendered by Shopify and
      // carry no grid_list, so without this every filter change in list view
      // snapped back to grid. Back/forward keeps the view of the entry it restores.
      var currentView = gridListViewOf(window.location.href);
      if (!isPopstate && !gl && currentView) {
        cleanUrl.searchParams.set('grid_list', currentView);
      }
      url = cleanUrl.pathname + cleanUrl.search;
    } catch (e) {}

    var sectionId = getSectionId();
    // Fallback: if we can't find the section, do full page fetch
    if (!sectionId) {
      ajaxLoadCollectionFallback(url, isPopstate);
      return;
    }

    // Loading state — dim grid + show spinner
    var gridWrapper = document.querySelector('.productgrid--wrapper');
    var gridContent = document.querySelector('[data-html="productgrid-items"]');
    var dimTarget = gridContent || gridWrapper;
    if (dimTarget) {
      dimTarget.style.opacity = '0.4';
      dimTarget.style.transition = 'opacity 0.15s ease';
      dimTarget.style.pointerEvents = 'none';
      dimTarget.classList.add('is-loading');
    }

    // Use Shopify's sections API — only fetches the collection section
    var fetchUrl = url + (url.indexOf('?') !== -1 ? '&' : '?') + 'sections=' + sectionId;

    fetch(fetchUrl, { credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var newHTML = data[sectionId];
        if (!newHTML) { window.location.href = url; return; }

        var doc = new DOMParser().parseFromString(newHTML, 'text/html');

        // Save accordion collapsed/expanded states before DOM swap
        var savedAccordionStates = saveAccordionStates();

        // Swap sidebar (filters + active pills)
        var newSidebar = doc.querySelector('[data-html="sidebar"]');
        var currentSidebar = document.querySelector('[data-html="sidebar"]');
        if (newSidebar && currentSidebar) {
          currentSidebar.innerHTML = newSidebar.innerHTML;
        }

        // Swap product grid items
        var newGrid = doc.querySelector('[data-html="productgrid-items"]');
        var currentGrid = document.querySelector('[data-html="productgrid-items"]');
        if (newGrid && currentGrid) {
          currentGrid.innerHTML = newGrid.innerHTML;
        } else {
          // A zero-result response omits [data-html="productgrid-items"] entirely and
          // renders the "no products" state in its place, so the guard above skipped
          // the swap and left the PREVIOUS products on screen — items that do not
          // match the filter the customer just applied. Verified: filtering heaters to
          // under $625 returns a 43KB section with zero productgrid-items containers
          // and one "no products" message, yet the grid still showed 24 products.
          //
          // .productgrid--wrapper is the nearest container present in BOTH the
          // populated and empty states, so swapping it also covers the way back
          // (empty → results), where currentGrid is the missing one.
          var newWrapper = doc.querySelector('.productgrid--wrapper');
          var currentWrapper = document.querySelector('.productgrid--wrapper');
          if (newWrapper && currentWrapper) {
            currentWrapper.innerHTML = newWrapper.innerHTML;
          }
        }

        // Swap utilities bar (sort, view, product count)
        var newUtils = doc.querySelector('.productgrid--utils');
        var currentUtils = document.querySelector('.productgrid--utils');
        if (newUtils && currentUtils) {
          currentUtils.innerHTML = newUtils.innerHTML;
        }

        // Swap pagination (Empire uses .pagination--container, not .pagination)
        var newPagination = doc.querySelector('.pagination--container');
        var currentPagination = document.querySelector('.pagination--container');
        if (newPagination && currentPagination) {
          currentPagination.innerHTML = newPagination.innerHTML;
        } else if (currentPagination && !newPagination) {
          currentPagination.innerHTML = '';
        }

        // NOTE: Do NOT swap .productgrid--masthead — the page has two elements
        // with that class. The first is the custom liquid section with the rich
        // H1, stars, description, and subcollection images. The section response
        // only contains a bare H1, so swapping would wipe out the custom content.
        // The masthead never changes with filters, so it doesn't need updating.

        // Update desktop filter pills (.productgrid--filters)
        // This container only exists when filters are active, so handle insert/remove too
        var newDesktopFilters = doc.querySelector('.productgrid--filters');
        var currentDesktopFilters = document.querySelector('.productgrid--filters');
        var currentGridItems = document.querySelector('[data-html="productgrid-items"]');

        if (newDesktopFilters && currentDesktopFilters) {
          // Both exist: update content
          currentDesktopFilters.innerHTML = newDesktopFilters.innerHTML;
        } else if (newDesktopFilters && !currentDesktopFilters && currentGridItems) {
          // New pills to insert (filter was just activated)
          var clone = newDesktopFilters.cloneNode(true);
          currentGridItems.parentNode.insertBefore(clone, currentGridItems);
        } else if (!newDesktopFilters && currentDesktopFilters) {
          // Pills to remove (all filters cleared)
          currentDesktopFilters.remove();
        }

        // Update URL without reload
        if (!isPopstate) {
          history.pushState({ filterAjax: true }, '', url);
        }

        // Re-init all JS on the new DOM
        initAll();
        initAccordions();
        restoreAccordionStates(savedAccordionStates);

        // Re-bind sort select handler on new DOM
        // The capture-phase listener on the parent was lost when the utils bar
        // innerHTML was swapped. Re-attach it on the new parent.
        var sortSelect = document.querySelector('[data-productgrid-trigger-sort]');
        if (sortSelect) {
          var sortParent = sortSelect.parentNode;
          if (sortParent) {
            sortParent.addEventListener('change', function (e) {
              if (e.target && e.target.hasAttribute('data-productgrid-trigger-sort')) {
                e.stopImmediatePropagation();
                var u = new URL(window.location.href);
                u.searchParams.set('sort_by', e.target.value);
                if (ajaxEnabled) { ajaxLoadCollection(u.toString()); }
                else { saveScrollPosition(); window.location.href = u.toString(); }
              }
            }, true);
          }
          sortSelect.setAttribute('data-sort-bound', 'ajax');
        }

        // Remove loading state
        if (dimTarget) {
          dimTarget.style.opacity = '';
          dimTarget.style.transition = '';
          dimTarget.style.pointerEvents = '';
          dimTarget.classList.remove('is-loading');
        }

        // Reinitialize theme's responsive image handler (rimg) after AJAX content swap
        // Without this, newly loaded product images stay as gray canvas placeholders
        // Wrapped in try/catch because Empire's _initAnimations handler crashes when
        // animation elements from the full page aren't present in the AJAX partial.
        var sectionEl = document.querySelector('#shopify-section-' + sectionId);
        if (sectionEl) {
          try {
            sectionEl.dispatchEvent(new CustomEvent('shopify:section:load', {
              bubbles: true,
              detail: { sectionId: sectionId }
            }));
          } catch (e) {
            // Empire's _initAnimations may throw — safe to ignore
          }
        }

        // The swapped utils bar comes back with the server's default view
        // marked active, and the grid wrapper class is not part of the swap.
        // Runs after the section:load re-init above, whose _checkListView fills
        // the pagination placeholders again (compounding list-view=list-view).
        renderGridListView(gridListViewOf(url) || defaultGridListView());
        syncPaginationLinks();

        // Re-ensure Shopify.queryParams for Empire's code
        if (typeof Shopify !== 'undefined') Shopify.queryParams = {};
        var allowed = ['view', 'sort_by', 'grid_list'];
        var search = url.indexOf('?') !== -1 ? url.split('?')[1] : '';
        if (search) {
          search.split('&').forEach(function (s) {
            var parts = s.split('=');
            if (parts.length > 1 && allowed.indexOf(parts[0]) !== -1) {
              Shopify.queryParams[decodeURIComponent(parts[0])] = decodeURIComponent(parts[1]);
            }
          });
        }
      })
      .catch(function () {
        // Network error → fall back to full page load
        window.location.href = url;
      });
  }

  // Fallback: fetch full page if section ID can't be determined
  function ajaxLoadCollectionFallback(url, isPopstate) {
    var outer = document.querySelector('.productgrid--outer');
    if (!outer) { window.location.href = url; return; }

    var grid = outer.querySelector('.productgrid--content') || outer;
    grid.style.opacity = '0.4';
    grid.style.transition = 'opacity 0.15s ease';
    grid.style.pointerEvents = 'none';
    grid.classList.add('is-loading');

    var savedAccordionStates = saveAccordionStates();

    fetch(url, { credentials: 'same-origin' })
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newOuter = doc.querySelector('.productgrid--outer');
        if (!newOuter) { window.location.href = url; return; }
        outer.innerHTML = newOuter.innerHTML;
        if (!isPopstate) { history.pushState({ filterAjax: true }, '', url); }
        initAll();
        initAccordions();
        restoreAccordionStates(savedAccordionStates);
      })
      .catch(function () { window.location.href = url; });
  }

  function initDesktopAjax() {
    // Only on desktop collection pages
    if (window.innerWidth <= 859) return;
    if (!document.querySelector('.productgrid--outer')) return;

    ajaxEnabled = true;

    // Intercept filter link clicks, pill clicks, clear-all
    // Uses capture phase + stopImmediatePropagation to block Empire's Kn handler
    document.addEventListener('click', function (e) {
      if (!ajaxEnabled || deferredMode) return;

      // Filter links (checkboxes / pills)
      var link = e.target.closest('a[data-filter-input]');
      if (link && !link.hasAttribute('data-disabled') && link.getAttribute('href') !== '#') {
        e.preventDefault();
        e.stopImmediatePropagation();

        // Get URL from data-url (Empire's attribute) or href
        var targetUrl = link.getAttribute('data-url') || link.getAttribute('href');
        if (targetUrl) {
          // data-url might be just a query string or URL-encoded
          if (targetUrl.charAt(0) === '%') targetUrl = decodeURIComponent(targetUrl);
          if (targetUrl.charAt(0) !== '/') targetUrl = location.pathname + '?' + targetUrl;

          // Preserve sort_by/view params
          var currentParams = new URLSearchParams(location.search);
          var targetUrlObj = new URL(targetUrl, location.origin);
          ['sort_by', 'view', 'grid_list'].forEach(function (key) {
            if (currentParams.has(key) && !targetUrlObj.searchParams.has(key)) {
              targetUrlObj.searchParams.set(key, currentParams.get(key));
            }
          });
          ajaxLoadCollection(targetUrlObj.pathname + targetUrlObj.search);
        }
        return;
      }

      // "Clear all" in the title row
      var clearLink = e.target.closest('.filter-clear-all-link');
      if (clearLink) {
        e.preventDefault();
        e.stopImmediatePropagation();
        ajaxLoadCollection(clearLink.getAttribute('href'));
        return;
      }

      // "Clear all" pill (data-clear-all-pill)
      var clearPill = e.target.closest('[data-clear-all-pill]');
      if (clearPill) {
        e.preventDefault();
        e.stopImmediatePropagation();
        ajaxLoadCollection(clearPill.getAttribute('href'));
        return;
      }

      // Active filter pill X buttons (remove individual filter)
      var pillRemove = e.target.closest('.active-filter-group__item');
      if (pillRemove) {
        var pillLink = pillRemove.closest('a');
        if (pillLink && pillLink.getAttribute('href') && pillLink.getAttribute('href') !== '#') {
          e.preventDefault();
          e.stopImmediatePropagation();
          ajaxLoadCollection(pillLink.getAttribute('href'));
          return;
        }
      }
    }, true);

    // Handle browser back / forward
    window.addEventListener('popstate', function () {
      ajaxLoadCollection(window.location.href, true);
    });

    // Store initial history state so popstate knows about it
    history.replaceState({ filterAjax: true }, '', window.location.href);

    // Bind sort select on first load
    // Empire binds a jQuery change.collection handler directly on the select
    // that triggers a full page reload — and re-binds it AFTER our code runs.
    // Solution: use a CAPTURE-phase listener on the parent that fires first,
    // calls stopImmediatePropagation to block Empire's handler, then does AJAX.
    var sortSelect = document.querySelector('[data-productgrid-trigger-sort]');
    if (sortSelect) {
      var sortParent = sortSelect.parentNode;
      if (sortParent) {
        sortParent.addEventListener('change', function (e) {
          if (e.target && e.target.hasAttribute('data-productgrid-trigger-sort')) {
            e.stopImmediatePropagation();
            var u = new URL(window.location.href);
            u.searchParams.set('sort_by', e.target.value);
            ajaxLoadCollection(u.toString());
          }
        }, true); // CAPTURE phase — fires before jQuery's bubble-phase handler
      }
      sortSelect.setAttribute('data-sort-bound', 'ajax');
    }
  }

  // ════════════════════════════════════════════
  // Mobile scroll-save fallback
  // For any non-AJAX navigations (mobile filter
  // apply, mobile sort, etc.)
  // ════════════════════════════════════════════
  function initMobileScrollSave() {
    if (window.innerWidth > 859) return; // desktop uses AJAX
    document.addEventListener('click', function (e) {
      if (deferredMode) return;
      var link = e.target.closest('a[data-filter-input]');
      if (link && !link.hasAttribute('data-disabled')) { saveScrollPosition(); return; }
      var cl = e.target.closest('[data-clear-all-pill], .filter-clear-all-link');
      if (cl) saveScrollPosition();
    }, true);
  }

  // ════════════════════════════════════════════
  // Grid / list view
  // The view lives in ?grid_list, which Empire reads on load. The toggle
  // switches in place (no reload) and writes the URL; ajaxLoadCollection,
  // the pagination links and the mobile filter drawer carry it forward.
  // Only a non-default view is written, so default URLs stay clean.
  // Classes go on .productgrid--outer: productgrid-gridview / -listview.
  // ════════════════════════════════════════════
  function gridListViewOf(url) {
    try {
      // Empire's placeholder fill can compound it: list-view=list-view
      return (new URL(url, location.origin).searchParams.get('grid_list') || '').split('=')[0];
    } catch (e) {
      return '';
    }
  }

  function defaultGridListView() {
    var data = document.querySelector('script[data-section-data][data-section-type^="static-collection"]');
    try {
      return JSON.parse(data.textContent).context.grid_list || 'grid-view';
    } catch (e) {
      return 'grid-view';
    }
  }

  function renderGridListView(mode) {
    // The collection masthead also renders a .productgrid--outer, earlier in
    // the page; the product grid's own is the one around .productgrid--wrapper.
    var wrapper = document.querySelector('.productgrid--wrapper');
    var outer = wrapper && wrapper.closest('.productgrid--outer');
    if (outer) {
      outer.classList.toggle('productgrid-listview', mode === 'list-view');
      outer.classList.toggle('productgrid-gridview', mode !== 'list-view');
    }
    var buttons = document.querySelectorAll('[data-collection-view]');
    for (var i = 0; i < buttons.length; i++) {
      var isActive = buttons[i].getAttribute('data-collection-view') === mode;
      buttons[i].classList.toggle('active', isActive);
      buttons[i].setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }
  }

  function setGridListView(mode) {
    renderGridListView(mode);
    var url = new URL(window.location.href);
    if (mode === defaultGridListView()) url.searchParams.delete('grid_list');
    else url.searchParams.set('grid_list', mode);
    history.replaceState({ filterAjax: true }, '', url.toString());
    // Empire's own filter/sort URL builders read this.
    if (window.Shopify && Shopify.queryParams) {
      if (url.searchParams.has('grid_list')) Shopify.queryParams.grid_list = mode;
      else delete Shopify.queryParams.grid_list;
    }
    syncPaginationLinks();
  }

  function initViewToggle() {
    // CAPTURE phase: Empire binds a jQuery click handler directly on each
    // button that reloads the page; stop the event before it gets there.
    document.addEventListener('click', function (e) {
      var viewBtn = e.target.closest('[data-collection-view]');
      if (!viewBtn) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();
      setGridListView(viewBtn.getAttribute('data-collection-view'));
    }, true);
  }

  // Empire's pagination hrefs end in a bare `&grid_list` placeholder that its
  // section JS fills (and can compound). Rewrite them to the current view.
  function syncPaginationLinks() {
    var view = gridListViewOf(window.location.href);
    var links = document.querySelectorAll('.pagination--container a[href]');
    for (var i = 0; i < links.length; i++) {
      try {
        var pUrl = new URL(links[i].href, location.origin);
        var before = pUrl.search;
        pUrl.searchParams.delete('grid_list');
        if (view) pUrl.searchParams.set('grid_list', view);
        if (pUrl.search !== before) links[i].href = pUrl.pathname + pUrl.search + pUrl.hash;
      } catch (e) {}
    }
  }

  // ════════════════════════════════════════════
  // Bootstrap
  // ════════════════════════════════════════════
  function bootAll() {
    initAll();
    initAccordions();
    initMobileFilterAndSort();
    initDesktopAjax();
    initMobileScrollSave();
    initViewToggle();
    // Sets aria-pressed, which the server markup lacks. Search pages load this
    // file too but have no toggle, so leave their grid classes alone.
    if (document.querySelector('[data-collection-view]')) {
      renderGridListView(gridListViewOf(window.location.href) || defaultGridListView());
    }
    syncPaginationLinks();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }
  // Empire's collection JS runs after this file and fills the pagination
  // placeholders; normalise them once everything has run.
  window.addEventListener('load', syncPaginationLinks);

  // Re-init after theme AJAX DOM updates (e.g. pagination)
  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      if (mutations[i].addedNodes.length) {
        for (var j = 0; j < mutations[i].addedNodes.length; j++) {
          var node = mutations[i].addedNodes[j];
          if (node.nodeType === 1 && (node.querySelector('[data-range-slider]') || node.matches('[data-range-slider]') || node.querySelector('[data-filter-group-trigger]'))) {
            initAll();
            initAccordions();
            return;
          }
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();
